import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, googleProvider, firestore } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import './LandingPage.css'; // Import your styles
import Nav from './Nav.js';
import Footer from './Footer.js';
import ReactGA from 'react-ga4';
import InstallPWA from './InstallPWA.js';
import Calendar from './Calender.js';
import Loader from './Loader.js';
import { track as mpTrack, initAnalytics } from "../analytics/mixpanel"; // from the tiny helper we made
import TransitCarousel from './TransitCarousel';
import CosmicSnapshot from './CosmicSnapshot';

const LandingPage = () => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const navigate = useNavigate();
  const words = ['life choices', 'career paths', 'finances', 'marriage details'];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(localStorage.getItem('userId'));
  // Important: Default to false if no user is found, to prevent FCP delays
  const [isLoading, setIsLoading] = useState(!!localStorage.getItem('userId'));
  const [platform, setPlatform] = useState('Web');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallSection, setShowInstallSection] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (userId) {
      ReactGA.set({ user_id: userId }); // Sets globally
    }
  }, [userId]);

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      if (userId) {
        try {
          const userRef = doc(firestore, 'users', userId);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            setUser(userDoc.data());
          } else {
            setUser(null);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setIsLoading(false);
    };
    fetchUserData();
  }, [userId]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
    }, 2000);

    return () => clearInterval(intervalId);
  }, [words.length]);

  useEffect(() => {
    const detectPlatform = () => {
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      return isPWA ? 'PWA' : 'Web';
    };
    setPlatform(detectPlatform());
  }, []);

  // Enhanced PWA detection with immediate feedback
  useEffect(() => {
    // Check for iOS devices
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

    // Immediate PWA check
    const isRunningAsPWA = () => {
      return window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone ||
        document.referrer.includes('android-app://');
    };

    // Initial check
    if (!isRunningAsPWA()) {
      setShowInstallSection(true);
    }

    const beforeInstallHandler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallSection(true);
      localStorage.removeItem('installDismissed');
    };

    const installedHandler = () => {
      setShowInstallSection(false);
      localStorage.setItem('installDismissed', 'true');
    };

    window.addEventListener('beforeinstallprompt', beforeInstallHandler);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstallHandler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstall = async (source = 'unknown') => {
    setIsInstalling(true);

    try {
      if (deferredPrompt) {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;

        if (choice.outcome === 'accepted') {
          trackAnalyticsEvent('install_clicked', {
            install_source: source,
            device_type: isIOS ? 'iOS' : (getPlatform() === 'PWA' ? 'PWA' : 'Android/Desktop'),
            status: 'accepted'
          });
          console.log('User installed the app');
        } else {
          trackAnalyticsEvent('install_clicked', {
            install_source: source,
            device_type: isIOS ? 'iOS' : (getPlatform() === 'PWA' ? 'PWA' : 'Android/Desktop'),
            status: 'dismissed'
          });
          console.log('User dismissed the install prompt');
        }
        setDeferredPrompt(null);
      } else if (isIOS) {
        // iOS specific instructions
        trackAnalyticsEvent('install_clicked', {
          install_source: source,
          device_type: 'iOS',
          status: 'instruction_shown'
        });
        alert(`To install:
1. Tap the Share icon 
2. Select "Add to Home Screen"
3. Tap "Add" in the top right`);
      } else {
        // Android/Desktop fallback
        trackAnalyticsEvent('install_clicked', {
          install_source: source,
          device_type: 'Android/Desktop',
          status: 'instruction_shown'
        });
        alert('Look for "Install" or "Add to Home Screen" in your browser menu');
      }
    } catch (error) {
      console.error('Installation error:', error);
      trackAnalyticsEvent('install_error', {
        error_message: error.message,
        install_source: source
      });
    } finally {
      setIsInstalling(false);
    }
  };

  const getPlatform = () => {
    if (window.matchMedia('(display-mode: standalone').matches || window.navigator.standalone) {
      return 'PWA';
    }
    return 'Website';
  };

  const trackEvent = (category, action, label = '') => {
    // Function is depricated
    // ReactGA.event({
    //   category,
    //   action: action + " - " + getPlatform(),
    //   label,
    //   platform: getPlatform(),
    // });
  };

  const trackAnalyticsEvent = (eventName, params = {}) => {
    const props = {
      platform: getPlatform(),  // keep this if you like it in both tools
      ...params,
    };

    // GA4
    ReactGA.event(eventName, props);
    // Mixpanel
    mpTrack(eventName, props);
  };

  const handleChatNowClick = (location = 'Hero Section') => {
    // trackEvent(`User Onboarding`, `Chat Initiated`, 'Home Page Primary CTA'); // Deprecated
    trackAnalyticsEvent('chat_initiated', {
      source: 'home_page',
      mode: 'personal',
      button_text: location === 'Hero Section' ? 'Start free chat' : 'Try It Free',
      button_location: location
    });
    navigate('/chat')
  };

  const navigateToChat = (mode) => {
    if (mode === 'relationship') {
      trackEvent(`Relationship Chat Clicked - Top Widget`, `Relationship Chat Clicked (Top Widget)`, 'Home screen widget section');
      trackAnalyticsEvent('chat_initiated', {
        source: 'home_page',
        mode: mode
      });
    }
    if (mode === 'personal') {
      trackEvent(`Personal Chat Clicked - Top Widget`, `Personal Chat Clicked (Top Widget)`, 'Home screen widget section');
      trackAnalyticsEvent('chat_initiated', {
        source: 'home_page',
        mode: mode
      });
    }
    navigate('/chat' + (mode ? `?mode=${mode}` : ''));
  }

  const navigateToMiniReports = () => {
    trackAnalyticsEvent('mini_reports_clicked', {
      source: 'home_page'
    });
    navigate('/mini-reports');
  }

  const navigateToMyCharts = (source = 'home_page') => {
    trackAnalyticsEvent('my_charts_entry_clicked', {
      source: 'home_page',
      entry_point: source
    });
    navigate('/my-chart');
  }

  const navigateToHoroscope = () => {
    trackEvent(`Horoscope Clicked - Top Widget`, `Horoscope Clicked (Top Widget)`, 'Home screen widget section');
    trackAnalyticsEvent('view_horoscope_clicked', {
      source: 'home_page',
      zodiacSign: user?.astro_data?.astro_details?.ascendant || 'unknown',
    });
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = (today.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
    const dd = today.getDate().toString().padStart(2, '0');
    const formattedDate = `${yyyy}-${mm}-${dd}`;

    navigate(`/horoscope/${user.astro_data.astro_details.ascendant}/${formattedDate}/daily-horoscope`);
  };


  const navigateToPanchang = () => {
    trackEvent(`Panchang Clicked - Top Widget`, `Panchang Clicked (Top Widget)`, 'Home screen widget section');
    trackAnalyticsEvent('view_panchang_clicked', {
      source: 'home_page'
    });
    const panchangSection = document.getElementById('panchang-section');
    if (panchangSection) {
      panchangSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  if (isLoading) {
    return (
      <Loader />
    );
  }

  return (
    <div>
      <Nav guestLanding={!user} />
      {!user && (
        <>
          {/* ═══ NEW HERO ═══ */}
          <section className="nhp-hero">
            <div className="nhp-hero-particles">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="nhp-particle"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 5}s`,
                    animationDuration: `${4 + Math.random() * 4}s`,
                  }}
                />
              ))}
            </div>
            <div className="nhp-hero-glow" />
            <div className="nhp-hero-content">
              <span className="nhp-badge">🔮 Simple Vedic Astrology</span>
              <h1>
                Ask your question. <br /> <span className="nhp-gold">Get a clear answer.</span>
              </h1>
              <p className="nhp-hero-sub">
                Career, marriage, money, health, or any life question. Vedic Rashi reads your kundli and explains the timing in simple words.
              </p>
              <button
                onClick={handleChatNowClick}
                className="nhp-hero-cta"
                disabled={buttonLoading}
              >
                {buttonLoading ? 'Loading...' : 'Ask My Question →'}
              </button>
              <div className="nhp-hero-note">
                <span className="nhp-note-icon">🛡️</span>
                No Fake Hope. Clear answers from your own chart, not vague predictions.
              </div>
              <div className="nhp-trust-row">
                <span className="nhp-trust-item">🪐 From your birth chart</span>
                <span className="nhp-trust-item">🔒 100% private</span>
                <span className="nhp-trust-item">🗣️ Simple words</span>
              </div>
            </div>
          </section>

          {/* ═══ GO DEEPER CARDS ═══ */}
          <section className="nhp-section nhp-deeper">
            <div className="nhp-section-inner">
              <div className="nhp-section-label">Why Vedic Rashi</div>
              <h2>Answers From Your Own Chart</h2>
              <p className="nhp-section-desc">
                Other apps give generic predictions. We read your actual birth chart and explain what it means for you, in plain words.
              </p>
              <div className="nhp-card-grid">
                <div className="nhp-card" onClick={() => navigateToChat('personal')}>
                  <div className="nhp-card-icon">📊</div>
                  <h3>Your Own Chart</h3>
                  <p>We map your exact birth details, not a generic sun-sign horoscope. Every answer comes from your kundli.</p>
                </div>
                <div className="nhp-card" onClick={() => navigateToChat('personal')}>
                  <div className="nhp-card-icon">⚡</div>
                  <h3>Plain Words</h3>
                  <p>No confusing jargon. Ask about career, marriage, money, or health and get an answer you can actually use.</p>
                </div>
                <div className="nhp-card" onClick={() => navigateToChat('personal')}>
                  <div className="nhp-card-icon">🧠</div>
                  <h3>Ask Anytime</h3>
                  <p>Your chart is ready whenever you have a question. No waiting for an appointment.</p>
                </div>
              </div>
            </div>
          </section>

          {/* ═══ WHAT YOU CAN EXPLORE ═══ */}
          <section className="nhp-features-section">
            <div className="nhp-features-inner">
              <div className="nhp-section-label">Explore</div>
              <h2>Everything You Can Ask About</h2>
              <div className="nhp-feature-grid">
                <div className="nhp-feature-card" onClick={() => navigateToChat('personal')}>
                  <div className="nhp-feat-icon">💬</div>
                  <div className="nhp-feat-info">
                    <h4>Ask Vedic Rashi</h4>
                    <p>Ask about career, money, family, or any life question and get an answer from your own chart</p>
                  </div>
                </div>
                <div className="nhp-feature-card" onClick={() => navigateToChat('love')}>
                  <div className="nhp-feat-icon">❤️</div>
                  <div className="nhp-feat-info">
                    <h4>Relationship Guide</h4>
                    <p>Understand your relationship and the right timing for milestones</p>
                  </div>
                </div>
                <div className="nhp-feature-card" onClick={() => { trackAnalyticsEvent('kundli_matching_clicked', { source: 'home_page' }); navigate('/kundli-analysis'); }}>
                  <div className="nhp-feat-icon">💑</div>
                  <div className="nhp-feat-info">
                    <h4>Kundli Matching</h4>
                    <p>18+ factor match analysis with Dosha checks, and you can ask questions about your match</p>
                  </div>
                </div>
                <div className="nhp-feature-card" onClick={() => navigate('/my-story')}>
                  <div className="nhp-feat-icon">📖</div>
                  <div className="nhp-feat-info">
                    <h4>My Story</h4>
                    <p>Your complete life story: personality, life phases, career, love, and key timings</p>
                  </div>
                </div>
                <div className="nhp-feature-card" onClick={() => navigateToMiniReports()}>
                  <div className="nhp-feat-icon">📊</div>
                  <div className="nhp-feat-info">
                    <h4>Mini Reports</h4>
                    <p>Focused reports for career, finances, marriage, health, and more</p>
                  </div>
                </div>
                <div className="nhp-feature-card" onClick={() => navigateToMyCharts('explore_card')}>
                  <div className="nhp-feat-icon">🪐</div>
                  <div className="nhp-feat-info">
                    <h4>My Charts</h4>
                    <p>View your Kundli charts, planet placements, and simple chart-by-chart readings</p>
                  </div>
                </div>
                <div className="nhp-feature-card" onClick={() => navigate('/my-horoscope')}>
                  <div className="nhp-feat-icon">🔮</div>
                  <div className="nhp-feat-info">
                    <h4>Today's Reading</h4>
                    <p>Your personal reading for today, based on your actual chart, not your sun sign</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ═══ HOW IT WORKS ═══ */}
          <section className="nhp-section nhp-how">
            <div className="nhp-section-inner">
              <div className="nhp-section-label">How It Works</div>
              <h2>Three Simple Steps</h2>
              <div className="nhp-steps">
                <div className="nhp-step">
                  <div className="nhp-step-num">1</div>
                  <h4>Share Your Details</h4>
                  <p>Enter your birth date, time, and place. That's all we need.</p>
                </div>
                <div className="nhp-step-line" />
                <div className="nhp-step">
                  <div className="nhp-step-num">2</div>
                  <h4>We Read Your Chart</h4>
                  <p>We map your birth chart against 2,000+ astrological points and today's sky.</p>
                </div>
                <div className="nhp-step-line" />
                <div className="nhp-step">
                  <div className="nhp-step-num">3</div>
                  <h4>Get Clear Answers</h4>
                  <p>Ask any question and get a simple, honest answer from your own chart.</p>
                </div>
              </div>
            </div>
          </section>

          {/* ═══ TRUST BAR ═══ */}
          <div className="nhp-trust-bar">
            <div className="nhp-trust-bar-inner">
              <div className="nhp-trust-stat">
                <span className="nhp-trust-num">50,000+</span>
                <span className="nhp-trust-label">users</span>
              </div>
              <div className="nhp-trust-stat">
                <span className="nhp-trust-num">2,000+</span>
                <span className="nhp-trust-label">Astrological Data Points</span>
              </div>
              <div className="nhp-trust-stat">
                <span className="nhp-trust-num">100%</span>
                <span className="nhp-trust-label">Private & Secure</span>
              </div>
              <div className="nhp-trust-stat">
                <span className="nhp-trust-num">24/7</span>
                <span className="nhp-trust-label">Always Available</span>
              </div>
            </div>
          </div>

          {/* ═══ FINAL CTA ═══ */}
          <section className="nhp-final">
            <h2>Your Answers Are Here</h2>
            <p>Stop guessing. Ask your question and get a clear, honest answer from your own birth chart.</p>
            <button className="nhp-final-cta" onClick={() => handleChatNowClick('Final Section')}>
              ✨ Ask My Question
            </button>
          </section>
        </>
      )}

      {userId && user && (
        <div className="welcome-back-container">
          <div className="welcome-back-header">
            <h2>
              <img
                width="35px"
                src='https://images.emojiterra.com/google/noto-emoji/unicode-15/color/512px/1f44b.png'
                className="waving-hand"
                alt="Waving hand"
              />
              Welcome back, {user.name}!
            </h2>
          </div>

          {/* Cosmic Snapshot: Personalized Decision Dashboard */}
          <CosmicSnapshot user={user} />

          {/* Planetary Transits Widget - Below Snapshot for flow */}
          <TransitCarousel userId={userId} userData={user} />

          <div className="action-buttons">
            <div className="action-column">
              <div className="action-button" onClick={() => navigateToChat('personal')}>
                <span className="action-icon"><img src='https://www.astrosage.com/images/varta/chat.png' alt='chat' width="40px"></img></span>
                <span className="action-text"> Personal Chat</span>
              </div>
              <div className="action-button" onClick={() => navigateToChat('love')}>
                <span className="action-icon"><img src="/ic_heart.png" alt='panchang' width="40px"></img></span>
                <span className="action-text">Relationship Guide</span>
              </div>
            </div>
          </div>
          <div className="action-buttons">
            <div className="action-column">
              <div className="action-button" onClick={() => navigateToMiniReports()}>
                <span className="action-icon"><img src="https://www.astrosage.com/images/icons/ic_occult.png" alt='panchang' width="40px"></img></span>
                <span className="action-text">Mini Reports</span>
              </div>
              <div className="action-button" onClick={() => navigateToMyCharts('action_button')}>
                <span className="action-icon" style={{ fontSize: '2rem', width: '40px', height: '40px' }}>🪐</span>
                <span className="action-text">My Charts</span>
              </div>
            </div>
          </div>
          <div className="action-buttons">
            <div className="action-column">
              <div className="action-button" onClick={() => navigate('/my-horoscope')}>
                <span className="action-icon"><img src='https://www.astrosage.com/images/varta/horoscope.png' alt='horoscope' width="40px"></img></span>
                <span className="action-text">My Horoscope</span>
              </div>
              <div
                className="action-button"
                style={{ position: 'relative' }}
                onClick={() => { trackAnalyticsEvent('kundli_matching_clicked', { source: 'home_page' }); navigate('/kundli-matching'); }}
              >
                <div style={{ position: 'absolute', top: '-10px', right: '-10px', backgroundColor: '#ff4444', color: 'white', fontSize: '0.65rem', fontWeight: '700', padding: '4px 10px', borderRadius: '10px', zIndex: 1, boxShadow: '0 2px 6px rgba(255, 68, 68, 0.3)' }}>✨ NEW</div>
                <span className="action-icon" style={{ fontSize: '2rem', width: '40px', height: '40px' }}>💑</span>
                <span className="action-text">Kundli Matching</span>
              </div>
              {/* <div className="action-button" onClick={() => navigate('/transit-report')}>
                <span className="action-icon"><img src="https://www.astrosage.com/images/varta/panchang.png" alt='transit' width="40px"></img></span>
                {/* <span className="action-text">Transit Report</span>
              </div> */}
            </div>
          </div>


          {/* My Story */}
          <div className="my-story-section">
            <div className="my-story-card" onClick={() => navigate('/my-story')}>
              <div className="my-story-content">
                <div className="my-story-icon">📖</div>
                <div className="my-story-text">
                  <h3>My Story</h3>
                  <p>Discover your complete life journey. Personality, phases, career, love & key timings</p>
                </div>
              </div>
              <div className="my-story-action">
                <span>Read Your Story →</span>
              </div>
            </div>
          </div>

          <p className="trusted-line" style={{ margin: '8px 0', fontSize: '18px' }}>
            ✅ Trusted by 50,000+ users
          </p>
        </div>
      )}
      {/* <InstallPWA /> */}
      <Footer />
    </div>
  );
};

export default LandingPage;
